import { useEffect, useState, useRef } from "react";
import { useDialogFocus } from "../../../hooks/useDialogFocus";
import { useNavigate } from "react-router-dom";
import type { BulkOutreachResult, ConnectorResponse } from "../../../types/crm";
import { schoolBulkOutreach, getCrmTeamById } from "../../../services/crmTeams";
import { getConnectors } from "../../../services/crmConnectors";
import { ProspectTypeahead } from "../../../components/ProspectTypeahead";
import type { ProspectOption } from "../../../components/ProspectTypeahead";
import { ModalCloseButton } from "../../../components/ModalCloseButton";
import "./SchoolsBulkOutreachModal.css";

type Stage = "compose" | "confirm" | "sending" | "results";

type OutreachChannel = "Gmail" | "SMS" | "X";

// Mirrors CrmOutboundChannelResolver.ResolveCoachAsync on the API so the
// frontend's connector-required check matches the channel the backend will
// actually use at send time.
const resolveOutreachChannel = (c: {
  preferredContactMethod?: string;
  email?: string;
  phone?: string;
  xHandle?: string;
}): OutreachChannel | null => {
  const pref = c.preferredContactMethod;
  if (pref === "X" && c.xHandle?.trim()) return "X";
  if (pref === "Gmail" && c.email?.trim()) return "Gmail";
  if (pref === "Quo" && c.phone?.trim()) return "SMS";
  if (c.email?.trim()) return "Gmail";
  if (c.phone?.trim()) return "SMS";
  return null;
};

interface SchoolsBulkOutreachModalProps {
  isOpen: boolean;
  schoolIds: string[];
  onClose: () => void;
  onDone: () => void;
}

export const SchoolsBulkOutreachModal = ({ isOpen, schoolIds, onClose, onDone }: SchoolsBulkOutreachModalProps) => {
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>("compose");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [result, setResult] = useState<BulkOutreachResult | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [connectorErrors, setConnectorErrors] = useState<string[]>([]);
  const [validating, setValidating] = useState(false);
  const [selectedPlayers, setSelectedPlayers] = useState<ProspectOption[]>([]);

  useEffect(() => {
    if (!isOpen) {
      setStage("compose");
      setSubject("");
      setBody("");
      setResult(null);
      setSendError(null);
      setConnectorErrors([]);
      setValidating(false);
      setSelectedPlayers([]);
    }
  }, [isOpen]);

  // Pre-validate connectors when modal opens
  useEffect(() => {
    if (!isOpen || schoolIds.length === 0) return;

    let cancelled = false;
    setValidating(true);
    setConnectorErrors([]);

    (async () => {
      try {
        const [connectors, ...schoolDetails] = await Promise.all([
          getConnectors(),
          ...schoolIds.map((id) => getCrmTeamById(id)),
        ]);

        if (cancelled) return;

        const hasGmail = connectors.some((c: ConnectorResponse) => c.connectorType === "Gmail" && c.status === "Active");
        const hasQuo = connectors.some((c: ConnectorResponse) => c.connectorType === "Quo" && c.status === "Active");
        const hasX = connectors.some((c: ConnectorResponse) => c.connectorType === "Twitter" && c.status === "Active");

        const defaultCoaches = schoolDetails
          .map((school) => school.coaches.find((c) => c.id === school.defaultContactCoachId) ?? school.coaches[0])
          .filter((coach): coach is NonNullable<typeof coach> => !!coach);

        const channels = new Set(
          defaultCoaches.map(resolveOutreachChannel).filter((ch): ch is OutreachChannel => ch !== null)
        );

        const errors: string[] = [];
        if (channels.has("Gmail") && !hasGmail) errors.push("Connect your Gmail to send this mass outreach");
        if (channels.has("SMS") && !hasQuo) errors.push("Connect your Phone (Quo) to send this mass outreach");
        if (channels.has("X") && !hasX) errors.push("Connect your X account to send this mass outreach");
        setConnectorErrors(errors);
      } catch {
        // Non-blocking — allow the user to proceed if validation fails
      } finally {
        if (!cancelled) setValidating(false);
      }
    })();

    return () => { cancelled = true; };
  }, [isOpen, schoolIds]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen && stage !== "sending") onClose();
    };
    if (isOpen) document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose, stage]);

  const handleSend = async () => {
    setStage("sending");
    setSendError(null);
    try {
      const data = await schoolBulkOutreach({
        schoolIds,
        body,
        subject: subject.trim() || undefined,
        crmProspectProfileIds: selectedPlayers.length > 0 ? selectedPlayers.map((p) => p.id) : undefined,
      });
      setResult(data);
      setStage("results");
    } catch {
      setSendError("Failed to send messages. Please try again.");
      setStage("confirm");
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && stage !== "sending") onClose();
  };

  const dialogRef = useRef<HTMLDivElement>(null);

  useDialogFocus({ isOpen, dialogRef, handleEscape: false });

  if (!isOpen) return null;

  const canProceed = !validating && connectorErrors.length === 0 && body.trim();

  return (
    <div className="schools-bulk-outreach-modal-overlay" onClick={handleOverlayClick}>
      <div ref={dialogRef} tabIndex={-1} className="schools-bulk-outreach-modal" role="dialog" aria-modal="true" aria-label="Send bulk outreach to schools">
        <div className="schools-bulk-outreach-modal__header">
          <h2 className="schools-bulk-outreach-modal__title">
            {stage === "compose" && `Send Outreach (${schoolIds.length} school${schoolIds.length !== 1 ? "s" : ""})`}
            {stage === "confirm" && "Confirm Send"}
            {stage === "sending" && "Sending..."}
            {stage === "results" && "Send Results"}
          </h2>
          {stage !== "sending" && <ModalCloseButton onClick={onClose} />}
        </div>

        <div className="schools-bulk-outreach-modal__body">
          {stage === "compose" && (
            <>
              <p className="schools-bulk-outreach-modal__recipients">
                Sending to the default contact of each selected school via their preferred channel.
              </p>

              {connectorErrors.length > 0 && (
                <div className="schools-bulk-outreach-modal__connector-errors">
                  {connectorErrors.map((err) => (
                    <div key={err} className="schools-bulk-outreach-modal__connector-error">
                      <span>{err}.</span>
                      <button
                        type="button"
                        className="schools-bulk-outreach-modal__connector-link"
                        onClick={() => navigate("/crm/connectors")}
                      >
                        Go to Connectors →
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="schools-bulk-outreach-modal__field">
                <label className="schools-bulk-outreach-modal__label">
                  On behalf of player(s) <span className="schools-bulk-outreach-modal__optional">(optional)</span>
                </label>
                {selectedPlayers.length > 0 && (
                  <div className="bulk-outreach-modal__player-chips">
                    {selectedPlayers.map((p) => (
                      <span key={p.id} className="bulk-outreach-modal__player-chip">
                        {p.displayName}
                        <button
                          type="button"
                          className="bulk-outreach-modal__player-chip-remove"
                          aria-label={`Remove ${p.displayName}`}
                          onClick={() =>
                            setSelectedPlayers((prev) => prev.filter((x) => x.id !== p.id))
                          }
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <ProspectTypeahead
                  value={null}
                  onSelect={(p) => {
                    if (!p) return;
                    setSelectedPlayers((prev) =>
                      prev.some((x) => x.id === p.id) ? prev : [...prev, p]
                    );
                  }}
                  placeholder="Search players to link..."
                />
              </div>
              <div className="schools-bulk-outreach-modal__field">
                <label className="schools-bulk-outreach-modal__label" htmlFor="school-outreach-subject">
                  Subject <span className="schools-bulk-outreach-modal__optional">(optional — for email)</span>
                </label>
                <input
                  id="school-outreach-subject"
                  type="text"
                  className="schools-bulk-outreach-modal__input"
                  placeholder="e.g. Recruiting Interest"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                />
              </div>
              <div className="schools-bulk-outreach-modal__field">
                <label className="schools-bulk-outreach-modal__label" htmlFor="school-outreach-body">
                  Message <span className="schools-bulk-outreach-modal__required">*</span>
                </label>
                <textarea
                  id="school-outreach-body"
                  className="schools-bulk-outreach-modal__textarea"
                  placeholder="Write your message here..."
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={6}
                />
              </div>
            </>
          )}

          {stage === "confirm" && (
            <>
              {sendError && <p className="schools-bulk-outreach-modal__error">{sendError}</p>}
              <p className="schools-bulk-outreach-modal__confirm-text">
                Sending to the default contact at <strong>{schoolIds.length}</strong> school{schoolIds.length !== 1 ? "s" : ""}.
                Each contact receives the message via their preferred channel (email or phone).
                {selectedPlayers.length > 0 && (
                  <> This outreach is on behalf of <strong>{selectedPlayers.map((p) => p.displayName).join(", ")}</strong>.</>
                )}
              </p>
              <div className="schools-bulk-outreach-modal__preview">
                {subject && <p className="schools-bulk-outreach-modal__preview-subject"><strong>Subject:</strong> {subject}</p>}
                <p className="schools-bulk-outreach-modal__preview-body">{body}</p>
              </div>
            </>
          )}

          {stage === "sending" && (
            <div className="schools-bulk-outreach-modal__sending">
              <div className="schools-bulk-outreach-modal__spinner" />
              <p>Sending to {schoolIds.length} school{schoolIds.length !== 1 ? "s" : ""}...</p>
            </div>
          )}

          {stage === "results" && result && (
            <>
              <div className="schools-bulk-outreach-modal__summary">
                <span className="schools-bulk-outreach-modal__summary-sent">{result.successCount} sent</span>
                {result.failureCount > 0 && (
                  <span className="schools-bulk-outreach-modal__summary-failed">{result.failureCount} failed</span>
                )}
              </div>
              <ul className="schools-bulk-outreach-modal__results-list">
                {result.results.map((r) => (
                  <li key={r.contactId} className={`schools-bulk-outreach-modal__result-item schools-bulk-outreach-modal__result-item--${r.status.toLowerCase()}`}>
                    <span className="schools-bulk-outreach-modal__result-name">{r.displayName}</span>
                    <span className="schools-bulk-outreach-modal__result-status">
                      {r.status === "Sent" && "Sent"}
                      {r.status === "Failed" && `Failed${r.error ? `: ${r.error}` : ""}`}
                      {r.status === "NoConnector" && "No connector"}
                      {r.status === "Unsendable" && (r.error ?? "No contact info")}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        <div className="schools-bulk-outreach-modal__footer">
          {stage === "compose" && (
            <>
              <button className="schools-bulk-outreach-modal__btn schools-bulk-outreach-modal__btn--secondary" onClick={onClose}>
                Cancel
              </button>
              <button
                className="schools-bulk-outreach-modal__btn schools-bulk-outreach-modal__btn--primary"
                onClick={() => setStage("confirm")}
                disabled={!canProceed}
              >
                {validating ? "Checking..." : "Review"}
              </button>
            </>
          )}
          {stage === "confirm" && (
            <>
              <button className="schools-bulk-outreach-modal__btn schools-bulk-outreach-modal__btn--secondary" onClick={() => setStage("compose")}>
                Back
              </button>
              <button className="schools-bulk-outreach-modal__btn schools-bulk-outreach-modal__btn--primary" onClick={handleSend}>
                Send to {schoolIds.length}
              </button>
            </>
          )}
          {stage === "results" && (
            <button className="schools-bulk-outreach-modal__btn schools-bulk-outreach-modal__btn--primary" onClick={onDone}>
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
